/**
 * ChangePasswordSettings — self-service password change for an authenticated
 * user. Posts to the existing `/api/auth/change-password` endpoint.
 *
 * Issue: #320
 */

import { useMemo, useState } from 'react';
import { changePassword } from '../../services/authApi';
import { evaluatePasswordStrength } from '../../utils/passwordStrength';

export function ChangePasswordSettings() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => evaluatePasswordStrength(newPassword), [newPassword]);
  const allRulesPass = strength.every((r) => r.ok);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const sameAsCurrent = newPassword.length > 0 && newPassword === currentPassword;
  const canSubmit =
    currentPassword.length > 0 &&
    allRulesPass &&
    passwordsMatch &&
    !sameAsCurrent;

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setMessage('Password updated.');
      reset();
    } catch (err: any) {
      const details = err?.response?.data?.details;
      const apiMsg = err?.response?.data?.error;
      if (Array.isArray(details) && details.length > 0) {
        setError(details.join(' • '));
      } else if (typeof apiMsg === 'string') {
        setError(apiMsg);
      } else {
        setError('Could not change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-ems-bg-card rounded-2xl border border-ems-border p-6 mb-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-ems-text-primary">Change Password</h2>
        <p className="text-sm text-ems-text-muted mt-1">
          Update the password you use to sign in. You will stay logged in on this device.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-4">
        {message && (
          <div className="p-3 rounded-lg bg-ems-green/5 border border-ems-green/20 text-ems-green text-sm">
            {message}
          </div>
        )}
        {error && (
          <div className="p-3 rounded-lg bg-ems-red/10 border border-ems-red/20 text-ems-red text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-ems-text-primary mb-1">Current password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-ems-text-primary mb-1">New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ems-text-primary mb-1">Confirm new password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green"
              required
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="mt-1 text-xs text-ems-red">Passwords do not match.</p>
            )}
          </div>
        </div>

        {sameAsCurrent && (
          <p className="text-xs text-ems-amber">New password must differ from your current password.</p>
        )}

        <ul className="space-y-1 text-xs">
          {strength.map((rule) => (
            <li key={rule.label} className={rule.ok ? 'text-ems-green' : 'text-ems-text-muted'}>
              {rule.ok ? '✓' : '•'} {rule.label}
            </li>
          ))}
        </ul>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading || !canSubmit}
            className="px-4 py-2 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 text-sm font-medium hover:bg-ems-forest/80 disabled:opacity-50"
          >
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </form>
    </section>
  );
}
